import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/assets";

const empty = {
  name: "",
  email: "",
  phone: "",
  event_date: "",
  event_location: "",
  num_guests: "",
  message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      toast.success("Grazie! Your request is on its way — we'll be in touch soon.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    { k: "name", label: "Name", type: "text", req: true },
    { k: "email", label: "Email", type: "email", req: true },
    { k: "phone", label: "Phone", type: "tel" },
    { k: "event_date", label: "Event date", type: "date" },
    { k: "event_location", label: "Event location", type: "text" },
    { k: "num_guests", label: "Number of guests", type: "number" },
  ];

  return (
    <section id="booking" className="py-20 md:py-32 bg-sc-espresso text-sc-cream">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <div className="eyebrow mb-4 text-sc-goldlight">Private events</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight">
            Book Our Gelato Cart for <em className="italic font-light text-white">Your Event</em>
          </h2>
          <p className="mt-5 text-base md:text-lg text-sc-cream/75 leading-relaxed">
            Want the Santa Chiara gelato cart for your party, private event, corporate event,
            wedding, or celebration? Contact us and bring authentic Italian gelato to your guests.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mt-14">
          <form onSubmit={submit} className="lg:col-span-3 space-y-7" data-testid="booking-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
              {fields.map((f) => (
                <div key={f.k} className={f.k === "event_location" ? "sm:col-span-2" : ""}>
                  <label className="eyebrow text-sc-goldlight block mb-2">
                    {f.label} {f.req && <span className="text-sc-sage">*</span>}
                  </label>
                  <input
                    type={f.type}
                    value={form[f.k]}
                    onChange={update(f.k)}
                    required={f.req}
                    data-testid={`booking-${f.k}`}
                    className="w-full bg-transparent border-b border-sc-cream/30 py-2.5 text-sc-cream placeholder-sc-cream/40 focus:outline-none focus:border-sc-goldlight transition-colors"
                  />
                </div>
              ))}
            </div>
            <div>
              <label className="eyebrow text-sc-goldlight block mb-2">Message</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={update("message")}
                data-testid="booking-message"
                placeholder="Tell us about your event…"
                className="w-full bg-transparent border-b border-sc-cream/30 py-2.5 text-sc-cream placeholder-sc-cream/40 focus:outline-none focus:border-sc-goldlight transition-colors resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-sc-cream text-sc-espresso font-medium hover:bg-sc-goldlight transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send Request"} <Send size={18} />
            </button>
          </form>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <div className="rounded-3xl bg-sc-cream/5 border border-sc-cream/15 p-8">
              <h3 className="font-serif-display text-2xl">Get in touch</h3>
              <p className="mt-3 text-sc-cream/70 text-sm leading-relaxed">
                Prefer to reach us directly? We'd love to hear about your celebration.
              </p>
              <a
                href={`mailto:${CONTACT.email}`}
                className="mt-6 flex items-center gap-3 text-sc-cream hover:text-sc-goldlight transition-colors"
                data-testid="contact-email"
              >
                <Mail size={18} className="text-sc-goldlight" /> {CONTACT.email}
              </a>
              <a
                href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                className="mt-4 flex items-center gap-3 text-sc-cream hover:text-sc-goldlight transition-colors"
                data-testid="contact-phone"
              >
                <Phone size={18} className="text-sc-goldlight" /> {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
